import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode.react";
import openSocket from "socket.io-client";
import {
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  FormControl,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PhoneAndroid as PhoneIcon,
  SignalCellular4Bar as SignalIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  QueuePlayNext as QueueIcon,
  Business as BusinessIcon,
  Reply as ReplyIcon,
  Save as SaveIcon,
  NotificationsActive as NotificationsActiveIcon,
  Search as SearchIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  History as HistoryIcon,
  PowerSettingsNew as PowerIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@material-ui/icons";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { green, red, orange, grey, blue } from "@material-ui/core/colors";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";
import { openApi } from "../../services/api";
import { sanitizeToastMessage } from "../../utils/toastPolicy";
import { formatPhoneNumber as formatPhone } from "../../utils/phoneFormat";

// Data local de hoje no formato YYYY-MM-DD (sem deslocamento de fuso do toISOString).
const getTodayLocalISO = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 600,
    width: "100%",
    borderRadius: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
  },
  logo: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  logoText: {
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 700,
    fontSize: "1.8rem",
    color: "#667eea",
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: theme.spacing(0.5, 1),
  },
  connectedChip: tintStyle(isDarkTheme(theme), green, 100, 800),
  disconnectedChip: tintStyle(isDarkTheme(theme), red, 100, 800),
  openingChip: tintStyle(isDarkTheme(theme), orange, 100, 800),
  qrcodeChip: tintStyle(isDarkTheme(theme), orange, 100, 800),
  // Tons 50–200 não mudam no tema escuro: lá o fundo vira tinta translúcida sob o texto branco
  connectedCard: {
    background: isDarkTheme(theme)
      ? `linear-gradient(135deg, ${alpha(green[500], 0.2)} 0%, ${alpha(green[500], 0.3)} 100%)`
      : `linear-gradient(135deg, ${green[50]} 0%, ${green[100]} 100%)`,
    borderRadius: 12,
    border: `1px solid ${isDarkTheme(theme) ? alpha(green[500], 0.4) : green[200]}`,
  },
  statsCard: {
    borderRadius: 12,
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[200]}`,
    height: "100%",
  },
  qrContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  // QR fica branco nos dois temas: a câmera precisa do contraste escuro-sobre-claro
  qrWrapper: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    display: "inline-block",
  },
  instructionsList: {
    paddingLeft: theme.spacing(2),
    "& li": {
      marginBottom: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  },
  phoneNumber: {
    fontFamily: "monospace",
    fontSize: "1.3rem",
    fontWeight: 600,
    letterSpacing: 1,
  },
  errorContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  loadingContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  notFoundContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  connectionName: {
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 600,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1),
  },
  avatarConnected: {
    backgroundColor: green[500],
    width: 56,
    height: 56,
  },
  autoRefreshText: {
    fontSize: "0.75rem",
    color: grey[500],
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  autoReplySection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${isDarkTheme(theme) ? alpha(blue[500], 0.4) : blue[200]}`,
    background: isDarkTheme(theme)
      ? `linear-gradient(135deg, ${alpha(blue[500], 0.2)} 0%, ${alpha(blue[500], 0.1)} 100%)`
      : `linear-gradient(135deg, ${blue[50]} 0%, #f0f4ff 100%)`,
  },
  autoReplySectionTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
    fontWeight: 600,
    color: isDarkTheme(theme) ? blue[300] : blue[800],
  },
  // ─── Botão ON/OFF (simula um interruptor real) ───
  powerToggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: isDarkTheme(theme) ? theme.palette.background.paper : "#fff",
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[200]}`,
  },
  powerToggle: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    width: 104,
    height: 42,
    borderRadius: 21,
    padding: 4,
    cursor: "pointer",
    userSelect: "none",
    border: "none",
    outline: "none",
    transition: "background 0.25s ease, box-shadow 0.25s ease",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.25)",
  },
  powerToggleOn: {
    background: `linear-gradient(135deg, ${green[400]} 0%, ${green[600]} 100%)`,
    justifyContent: "flex-end",
  },
  powerToggleOff: {
    background: `linear-gradient(135deg, ${red[300]} 0%, ${red[500]} 100%)`,
    justifyContent: "flex-start",
  },
  powerToggleDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  powerKnob: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  powerToggleLabel: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.8rem",
    letterSpacing: 1,
    textShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },
  powerToggleLabelOn: {
    left: 16,
  },
  powerToggleLabelOff: {
    right: 14,
  },
  autoReplyField: {
    marginBottom: theme.spacing(2),
  },
  replyTextRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  replyTextActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
  },
  replyTextHelper: {
    display: "block",
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(2),
  },
  autoReplyActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  alertBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    border: `1px solid ${isDarkTheme(theme) ? alpha(orange[500], 0.4) : orange[200]}`,
    background: isDarkTheme(theme)
      ? `linear-gradient(135deg, ${alpha(orange[500], 0.2)} 0%, ${alpha(orange[500], 0.1)} 100%)`
      : `linear-gradient(135deg, ${orange[50]} 0%, #fffaf3 100%)`,
  },
  historyBox: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px dashed ${isDarkTheme(theme) ? theme.palette.divider : grey[300]}`,
  },
  statCard: {
    flex: 1,
    minWidth: 90,
    textAlign: "center",
    padding: theme.spacing(1.5, 1),
    borderRadius: 12,
  },
  statCardSuccess: {
    background: isDarkTheme(theme) ? alpha(green[500], 0.2) : green[50],
    border: `1px solid ${isDarkTheme(theme) ? alpha(green[500], 0.4) : green[200]}`,
  },
  statCardFailed: {
    background: isDarkTheme(theme) ? alpha(red[500], 0.2) : red[50],
    border: `1px solid ${isDarkTheme(theme) ? alpha(red[500], 0.4) : red[200]}`,
  },
  statCardTotal: {
    background: isDarkTheme(theme) ? alpha(grey[500], 0.2) : grey[100],
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[300]}`,
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.1,
  },
  historyList: {
    marginTop: theme.spacing(2),
    maxHeight: 280,
    overflowY: "auto",
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[200]}`,
    borderRadius: 12,
  },
  historyItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[100]}`,
  },
}));

// Converte a chave VAPID (base64url) para Uint8Array exigido pelo PushManager
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const formatPhoneNumber = (number) => formatPhone(number, "N/A");

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// TTL local do QR: se o mesmo QR ficar exibido por mais que isso sem renovar nem
// conectar, marcamos como expirado no front (evita QR obsoleto e regeneração em loop).
const QR_LOCAL_TTL_MS = 60000;

const getStatusLabel = (status) => {
  const map = {
    CONNECTED: "Conectado",
    DISCONNECTED: "Desconectado",
    OPENING: "Conectando...",
    qrcode: "Aguardando QR Code",
  };
  return map[status] || status;
};

const PublicConnectionPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const { hash } = useParams();

  const [connectionData, setConnectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  // Login por número de telefone (pairing code): método e número informado.
  const [pubMethod, setPubMethod] = useState("qr");
  const [pubPhone, setPubPhone] = useState("");
  // Quando true, exibe o campo de número (editar) mesmo havendo um código gerado.
  const [pubEditingNumber, setPubEditingNumber] = useState(false);
  // Estado "conectando" preso por muito tempo → libera escape (gerar novo QR).
  const [connectingEscape, setConnectingEscape] = useState(false);
  // QR expirado localmente (TTL) — evita exibir QR obsoleto na página pública.
  const [qrStaleLocal, setQrStaleLocal] = useState(false);

  // Auto-reply state
  const [arConfig, setArConfig] = useState({
    enabled: false,
    groupJid: "",
    contactJid: "",
    contactJids: [],
    replyText: "",
    replyTexts: [""],
    ignorePatterns: "",
    disableAfterReply: false,
    alertEnabled: false,
  });
  const [alertBusy, setAlertBusy] = useState(false);
  const [arGroups, setArGroups] = useState([]);
  const [arParticipants, setArParticipants] = useState([]);
  const [arSaving, setArSaving] = useState(false);
  const [arToggling, setArToggling] = useState(false);
  const [arSyncing, setArSyncing] = useState(false);
  const [arSnackbar, setArSnackbar] = useState("");
  const [arLoaded, setArLoaded] = useState(false);

  // Histórico / contadores
  const [arStats, setArStats] = useState({ totals: { success: 0, failed: 0, total: 0 }, events: [] });
  const [arStatsLoading, setArStatsLoading] = useState(false);
  const [statsFrom, setStatsFrom] = useState(getTodayLocalISO);
  const [statsTo, setStatsTo] = useState(getTodayLocalISO);

  const pollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const autoGenAttemptedRef = useRef(false);
  // Identifica esta aba/navegador para o lock de "driver" da conexão.
  const clientIdRef = useRef(null);
  if (!clientIdRef.current) {
    clientIdRef.current = `pub_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
  const autoRefreshTimerRef = useRef(null);
  const connectingTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const lastAlertAtRef = useRef(null);
  const pushResyncedRef = useRef(false);

  // Função para buscar status da conexão
  const fetchStatus = useCallback(async () => {
    if (!hash || !isMountedRef.current) return null;
    try {
      const { data } = await openApi.get(`/public-connection/${hash}?clientId=${clientIdRef.current}`);
      if (isMountedRef.current) {
        if (data.success) {
          setConnectionData(data.data);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
        setLoading(false);
        return data?.success ? data.data : null;
      }
    } catch (err) {
      if (isMountedRef.current) {
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
        setLoading(false);
      }
    }
    return null;
  }, [hash]);

  // Carrega config de auto-resposta e lista de grupos
  const loadAutoReply = useCallback(async () => {
    if (!hash) return;
    try {
      const [cfgRes, groupsRes] = await Promise.all([
        openApi.get(`/public-connection/${hash}/autoreply`),
        openApi.get(`/public-connection/${hash}/groups`),
      ]);
      if (cfgRes.data.success) {
        const d = cfgRes.data.data || {};
        // Compat: garante que replyTexts seja sempre um array não-vazio.
        const replyTexts =
          Array.isArray(d.replyTexts) && d.replyTexts.length
            ? d.replyTexts
            : d.replyText
            ? [d.replyText]
            : [""];
        // Compat: garante contactJids como array (configs antigas têm só contactJid).
        const contactJids =
          Array.isArray(d.contactJids) && d.contactJids.length
            ? d.contactJids
            : d.contactJid
            ? [d.contactJid]
            : [];
        setArConfig(c => ({ ...c, ...d, replyTexts, contactJids }));
      }
      if (groupsRes.data.success) setArGroups(groupsRes.data.data);
      setArLoaded(true);
    } catch {
      setArLoaded(true);
    }
  }, [hash]);

  // Carrega histórico/contadores de auto-resposta (com filtro de datas)
  const loadAutoReplyStats = useCallback(async () => {
    if (!hash) return;
    setArStatsLoading(true);
    try {
      const params = [];
      if (statsFrom) params.push(`from=${statsFrom}`);
      if (statsTo) params.push(`to=${statsTo}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const { data } = await openApi.get(`/public-connection/${hash}/autoreply-events${qs}`);
      if (data.success) setArStats(data.data);
    } catch {
      /* ignora */
    } finally {
      setArStatsLoading(false);
    }
  }, [hash, statsFrom, statsTo]);

  // Mantém uma referência sempre atual de loadAutoReplyStats para que o efeito
  // do socket (abaixo) possa recarregar as stats sem depender dela nas deps —
  // assim o socket não reconecta a cada mudança de filtro de datas.
  const loadAutoReplyStatsRef = useRef(loadAutoReplyStats);
  useEffect(() => {
    loadAutoReplyStatsRef.current = loadAutoReplyStats;
  }, [loadAutoReplyStats]);

  // Carrega participantes quando grupo muda
  const loadParticipants = useCallback(async (groupJid) => {
    if (!hash || !groupJid) { setArParticipants([]); return; }
    try {
      const res = await openApi.get(
        `/public-connection/${hash}/participants?groupJid=${encodeURIComponent(groupJid)}`
      );
      if (res.data.success) setArParticipants(res.data.data);
    } catch {
      setArParticipants([]);
    }
  }, [hash]);

  // Gerar novo QR Code
  const handleGenerateQrCode = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setQrLoading(true);
    setActionError("");
    try {
      await openApi.post(`/public-connection/${hash}/generate-qr`, { clientId: clientIdRef.current });
    } catch (err) {
      if (!silent) {
        setActionError(
          sanitizeToastMessage(err?.response?.data?.message) || "Nao foi possivel gerar o QR Code agora. Tente novamente."
        );
      }
    } finally {
      if (!silent) setQrLoading(false);
    }
  }, [hash]);

  // Login por número de telefone: solicita o código de pareamento (8 dígitos).
  const handleRequestPairingCode = useCallback(async () => {
    const digits = (pubPhone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setActionError("Informe o número com DDI + DDD + número (ex.: 5511999998888).");
      return;
    }
    setQrLoading(true);
    setActionError("");
    try {
      await openApi.post(`/public-connection/${hash}/pairing-code`, { number: digits, clientId: clientIdRef.current });
      // Sai do modo de edição → o código novo (via polling) volta a ser exibido.
      setPubEditingNumber(false);
    } catch (err) {
      setActionError(
        sanitizeToastMessage(err?.response?.data?.message) || "Nao foi possivel gerar o codigo agora. Tente novamente."
      );
    } finally {
      setQrLoading(false);
    }
  }, [hash, pubPhone]);

  // "Puxar para cá": assume o controle (driver) desta conexão e gera aqui.
  const handleTakeOver = useCallback(() => {
    autoGenAttemptedRef.current = true;
    setPubEditingNumber(false);
    setPubMethod("qr");
    setActionError("");
    handleGenerateQrCode({ silent: false });
  }, [handleGenerateQrCode]);

  // Polling para atualizações em tempo real + auto-geração de QR ao acessar
  useEffect(() => {
    isMountedRef.current = true;

    const bootstrap = async () => {
      const data = await fetchStatus();
      const lockedByOther =
        data && data.activeClientId && data.activeClientId !== clientIdRef.current;
      if (
        data &&
        !autoGenAttemptedRef.current &&
        data.status !== "CONNECTED" &&
        !data.isConnected &&
        !lockedByOther
      ) {
        autoGenAttemptedRef.current = true;
        handleGenerateQrCode({ silent: true });
      }
      if (data?.hasAutoReplyFeature) {
        loadAutoReply();
        loadAutoReplyStats();
      }
    };

    bootstrap();

    pollIntervalRef.current = setInterval(fetchStatus, 2000);

    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (autoRefreshTimerRef.current) clearTimeout(autoRefreshTimerRef.current);
    };
  }, [fetchStatus, handleGenerateQrCode, loadAutoReply, loadAutoReplyStats]);

  // Carrega participantes quando o grupo selecionado muda
  useEffect(() => {
    if (arConfig.groupJid) loadParticipants(arConfig.groupJid);
  }, [arConfig.groupJid, loadParticipants]);

  // ─── WEBSOCKET (página pública, sem login) ───
  // Conecta um socket público (autenticado apenas pelo hash da conexão) para
  // receber atualizações em tempo real do estado da auto-resposta. Assim, quando
  // o sistema envia a resposta e desativa a auto-resposta ("desativar após enviar"),
  // o botão ON/OFF atualiza sozinho — sem esperar o polling.
  useEffect(() => {
    if (!hash) return undefined;

    const socket = openSocket(process.env.REACT_APP_BACKEND_URL, {
      transports: ["websocket", "polling"],
      query: { publicConnectionHash: hash },
      reconnection: true,
    });

    const handleAutoReplyUpdate = (payload) => {
      if (!payload || typeof payload.enabled === "undefined") return;
      setArConfig((c) => ({ ...c, enabled: !!payload.enabled }));
    };

    // Novo evento de auto-resposta gravado (ou rebaixado p/ falha): recarrega o
    // relatório "Desempenho e histórico" em tempo real, sem precisar de F5.
    const handleAutoReplyEvent = () => {
      loadAutoReplyStatsRef.current();
    };

    socket.on("public-connection-autoreply", handleAutoReplyUpdate);
    socket.on("public-connection-autoreply-event", handleAutoReplyEvent);

    return () => {
      socket.off("public-connection-autoreply", handleAutoReplyUpdate);
      socket.off("public-connection-autoreply-event", handleAutoReplyEvent);
      socket.disconnect();
    };
  }, [hash]);

  // Detecta estado "conectando" preso: o banco já marcou CONNECTED mas o socket
  // real ainda não confirmou (janela pós-leitura do QR / reconexão). Após 20s
  // nesse estado, libera um botão de escape para gerar um novo QR.
  useEffect(() => {
    const connecting =
      connectionData?.status === "CONNECTED" && !connectionData?.isConnected;
    if (!connecting) {
      setConnectingEscape(false);
      if (connectingTimerRef.current) {
        clearTimeout(connectingTimerRef.current);
        connectingTimerRef.current = null;
      }
      return undefined;
    }
    if (!connectingTimerRef.current) {
      connectingTimerRef.current = setTimeout(() => setConnectingEscape(true), 20000);
    }
    return undefined;
  }, [connectionData?.status, connectionData?.isConnected]);

  // Expiração local do QR na página pública: reinicia a cada novo QR. Se não
  // renovar nem conectar dentro do TTL, marca expirado (mostra botão manual).
  useEffect(() => {
    const q = connectionData?.qrcode;
    const isRealQr =
      connectionData?.status === "qrcode" &&
      q &&
      q !== "EXPIRED" &&
      !q.startsWith("PAIR:") &&
      !q.startsWith("COOLDOWN:");
    if (!isRealQr) {
      setQrStaleLocal(false);
      return undefined;
    }
    setQrStaleLocal(false);
    const timer = setTimeout(() => setQrStaleLocal(true), QR_LOCAL_TTL_MS);
    return () => clearTimeout(timer);
  }, [connectionData?.qrcode, connectionData?.status]);

  // Reconectar com sessão existente
  const handleReconnect = async () => {
    setQrLoading(true);
    setActionError("");
    try {
      await openApi.post(`/public-connection/${hash}/reconnect`);
    } catch (err) {
      setActionError(
        sanitizeToastMessage(err?.response?.data?.message) || "Nao foi possivel reconectar agora. Tente novamente."
      );
    } finally {
      setQrLoading(false);
    }
  };

  const handleArFieldChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setArConfig(c => {
      const next = { ...c, [field]: value };
      if (field === "groupJid") {
        next.contactJid = "";
        next.contactJids = [];
      }
      return next;
    });
  };

  // ─── Opções de texto de resposta (múltiplos modelos) ───
  const handleReplyTextChange = (index) => (e) => {
    const { value } = e.target;
    setArConfig(c => {
      const replyTexts = [...(c.replyTexts || [""])];
      replyTexts[index] = value;
      return { ...c, replyTexts };
    });
  };

  const handleAddReplyText = () => {
    setArConfig(c => ({ ...c, replyTexts: [...(c.replyTexts || [""]), ""] }));
  };

  const handleRemoveReplyText = (index) => {
    setArConfig(c => {
      const current = c.replyTexts || [""];
      if (current.length <= 1) return { ...c, replyTexts: [""] };
      return { ...c, replyTexts: current.filter((_, i) => i !== index) };
    });
  };

  const handleSaveAutoReply = async () => {
    setArSaving(true);
    try {
      await openApi.put(`/public-connection/${hash}/autoreply`, arConfig);
      setArSnackbar("Configuração salva com sucesso!");
    } catch {
      setArSnackbar("Erro ao salvar configuração.");
    } finally {
      setArSaving(false);
    }
  };

  // Aplica uma alteração de config IMEDIATAMENTE (sem precisar do botão Salvar).
  // Atualiza a tela de forma otimista e reverte se a API falhar.
  const persistArPatch = async (patch, { successMsg, errorMsg, busy } = {}) => {
    const previous = arConfig;
    const next = { ...arConfig, ...patch };
    setArConfig(next);
    if (busy) setArToggling(true);
    try {
      await openApi.put(`/public-connection/${hash}/autoreply`, next);
      if (successMsg) setArSnackbar(successMsg);
    } catch {
      setArConfig(previous);
      setArSnackbar(errorMsg || "Não foi possível salvar. Tente novamente.");
    } finally {
      if (busy) setArToggling(false);
    }
  };

  // Botão ON/OFF — liga/desliga a auto-resposta na hora.
  const handleTogglePower = () => {
    if (arToggling) return;
    const turningOn = !arConfig.enabled;
    persistArPatch(
      { enabled: turningOn },
      {
        busy: true,
        successMsg: turningOn ? "Auto-resposta ativada!" : "Auto-resposta desativada.",
        errorMsg: "Não foi possível alterar a auto-resposta. Tente novamente.",
      }
    );
  };

  // "Desativar automaticamente após enviar" — também aplica na hora.
  const handleToggleDisableAfter = (e) => {
    persistArPatch({ disableAfterReply: e.target.checked });
  };

  // ─── ALERTA SONORO / PUSH ───

  // "Desbloqueia" o áudio — precisa rodar dentro de um gesto do usuário (clique)
  const primeAudio = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) audioCtxRef.current = new AC();
      }
      if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
    } catch {
      /* ignora */
    }
  }, []);

  // Toca uma sequência de beeps (usado quando a página está aberta)
  const playBeep = useCallback(() => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [0, 0.45, 0.9].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.35, now + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.4);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.42);
      });
    } catch {
      /* ignora */
    }
  }, []);

  // Alerta local imediato (aba aberta): som + vibração + notificação
  const triggerLocalAlert = useCallback(
    (preview) => {
      playBeep();
      try {
        if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
      } catch {
        /* ignora */
      }
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          // eslint-disable-next-line no-new
          new Notification("Resposta automática enviada", {
            body: preview || "O sistema respondeu automaticamente no grupo.",
            icon: "/android-chrome-192x192.png",
          });
        }
      } catch {
        /* ignora */
      }
    },
    [playBeep]
  );

  // Ativa o alerta push (registra service worker + subscription no backend)
  const enablePushAlert = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setArSnackbar("Este navegador não suporta alertas em segundo plano.");
      return false;
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      setArSnackbar("Permissão de notificação negada — não foi possível ativar o alerta.");
      return false;
    }

    const reg = await navigator.serviceWorker.register("/autoreply-sw.js");
    await navigator.serviceWorker.ready;

    const { data: keyRes } = await openApi.get(`/public-connection/${hash}/push-key`);
    const publicKey = keyRes?.data?.publicKey;
    if (!publicKey) {
      setArSnackbar("Alerta push não está configurado no servidor.");
      return false;
    }

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await openApi.post(`/public-connection/${hash}/push-subscribe`, {
      subscription: subscription.toJSON ? subscription.toJSON() : subscription,
    });
    return true;
  }, [hash]);

  // Desativa o alerta push (remove subscription)
  const disablePushAlert = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator)) return;
      const reg = await navigator.serviceWorker.getRegistration("/autoreply-sw.js");
      if (!reg) return;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await openApi
          .post(`/public-connection/${hash}/push-unsubscribe`, {
            endpoint: subscription.endpoint,
          })
          .catch(() => {});
        await subscription.unsubscribe().catch(() => {});
      }
    } catch {
      /* ignora */
    }
  }, [hash]);

  // Liga/desliga o alerta — precisa do gesto do usuário para liberar áudio/permissão
  const handleAlertToggle = async (e) => {
    const checked = e.target.checked;
    setAlertBusy(true);
    try {
      if (checked) {
        primeAudio();
        const ok = await enablePushAlert();
        if (!ok) {
          setAlertBusy(false);
          return;
        }
        setArConfig((c) => ({ ...c, alertEnabled: true }));
        await openApi
          .put(`/public-connection/${hash}/autoreply`, { ...arConfig, alertEnabled: true })
          .catch(() => {});
        setArSnackbar("Alerta ativado! Você será avisado a cada resposta automática.");
      } else {
        await disablePushAlert();
        setArConfig((c) => ({ ...c, alertEnabled: false }));
        await openApi
          .put(`/public-connection/${hash}/autoreply`, { ...arConfig, alertEnabled: false })
          .catch(() => {});
        setArSnackbar("Alerta desativado.");
      }
    } catch (err) {
      setArSnackbar("Não foi possível alterar o alerta. Tente novamente.");
    } finally {
      setAlertBusy(false);
    }
  };

  // Reabriu a página com alerta já ativo: re-registra a subscription push
  // silenciosamente (permissão já concedida → não exige novo gesto).
  // Declarado após as funções para evitar TDZ no array de dependências.
  useEffect(() => {
    if (!arLoaded || pushResyncedRef.current) return;
    if (!arConfig.alertEnabled) return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    pushResyncedRef.current = true;
    enablePushAlert().catch(() => {});
  }, [arLoaded, arConfig.alertEnabled, enablePushAlert]);

  // Detecta novo envio de auto-resposta via polling e dispara alerta local
  // (som/vibração/notificação) quando a aba está aberta. Com a aba fechada,
  // quem alerta é o push (service worker).
  useEffect(() => {
    const at = connectionData?.lastAutoReplyAt;
    if (!at) return;
    // Primeira observação: define a baseline e NÃO alerta envios antigos.
    if (lastAlertAtRef.current === null) {
      lastAlertAtRef.current = at;
      return;
    }
    if (at !== lastAlertAtRef.current) {
      lastAlertAtRef.current = at;
      if (arConfig.alertEnabled) {
        triggerLocalAlert(connectionData?.lastAutoReplyPreview);
      }
    }
  }, [
    connectionData?.lastAutoReplyAt,
    connectionData?.lastAutoReplyPreview,
    arConfig.alertEnabled,
    triggerLocalAlert,
  ]);

  const handleSyncParticipants = async () => {
    if (!arConfig.groupJid) {
      setArSnackbar("Selecione um grupo primeiro.");
      return;
    }
    setArSyncing(true);
    try {
      const res = await openApi.post(
        `/public-connection/${hash}/sync-participants?groupJid=${encodeURIComponent(arConfig.groupJid)}`
      );
      if (res.data.success) {
        setArParticipants(res.data.data);
        setArSnackbar(`${res.data.data.length} participantes sincronizados!`);
      } else {
        setArSnackbar(res.data.message || "Erro ao sincronizar.");
      }
    } catch (err) {
      setArSnackbar(err?.response?.data?.message || "Erro ao sincronizar participantes.");
    } finally {
      setArSyncing(false);
    }
  };

  const participantLabel = (p) => {
    const name = p.participantName || "";
    const number = p.participantNumber ? `+${p.participantNumber}` : "";
    const jidShort = p.participantJid ? `…${p.participantJid.slice(-12)}` : "";
    const display = name || number || jidShort;
    const extra = name && number ? ` (${number})` : "";
    const role = p.isSuperAdmin ? " [admin]" : p.isAdmin ? " [admin]" : "";
    return `${display}${extra}${role}`;
  };

  const renderAutoReplySection = () => {
    if (!connectionData?.hasAutoReplyFeature) return null;

    return (
      <Box className={classes.autoReplySection}>
        <Box className={classes.autoReplySectionTitle}>
          <ReplyIcon style={{ color: isDark ? blue[300] : blue[700] }} />
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: isDark ? blue[300] : blue[800] }}>
            Resposta Automática de Grupo
          </Typography>
        </Box>

        {/* Botão ON/OFF — liga/desliga na hora, sem botão Salvar.
            Atualizado automaticamente pelo websocket. */}
        <Box className={classes.powerToggleRow}>
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {arConfig.enabled ? "Auto-resposta LIGADA" : "Auto-resposta DESLIGADA"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Toque no botão para ligar ou desligar — aplica na hora.
            </Typography>
          </Box>
          <button
            type="button"
            onClick={handleTogglePower}
            disabled={arToggling}
            aria-pressed={!!arConfig.enabled}
            title={arConfig.enabled ? "Desligar auto-resposta" : "Ligar auto-resposta"}
            className={`${classes.powerToggle} ${
              arConfig.enabled ? classes.powerToggleOn : classes.powerToggleOff
            } ${arToggling ? classes.powerToggleDisabled : ""}`}
          >
            <span
              className={`${classes.powerToggleLabel} ${
                arConfig.enabled ? classes.powerToggleLabelOn : classes.powerToggleLabelOff
              }`}
            >
              {arConfig.enabled ? "ON" : "OFF"}
            </span>
            <span className={classes.powerKnob}>
              {arToggling ? (
                <CircularProgress size={18} />
              ) : (
                <PowerIcon
                  fontSize="small"
                  style={{ color: arConfig.enabled ? green[600] : red[500] }}
                />
              )}
            </span>
          </button>
        </Box>

        <Autocomplete
          size="small"
          className={classes.autoReplyField}
          options={arGroups}
          getOptionLabel={(g) => g.name || g.jid || ""}
          getOptionSelected={(opt, val) => (opt.jid || "") === (val.jid || "")}
          value={arGroups.find((g) => (g.jid || "") === arConfig.groupJid) || null}
          onChange={(e, newVal) => {
            setArConfig((c) => ({
              ...c,
              groupJid: newVal ? newVal.jid || "" : "",
              contactJid: "",
              contactJids: [],
            }));
          }}
          noOptionsText="Nenhum grupo encontrado"
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Grupo monitorado"
              placeholder="Buscar grupo..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box display="flex" alignItems="flex-start" style={{ gap: 8, marginBottom: 16 }}>
          <Autocomplete
            multiple
            size="small"
            fullWidth
            style={{ flex: 1 }}
            disabled={!arConfig.groupJid}
            options={arParticipants}
            getOptionLabel={(p) => participantLabel(p)}
            getOptionSelected={(opt, val) =>
              (opt.participantJid || "") === (val.participantJid || "")
            }
            value={arParticipants.filter((p) =>
              (arConfig.contactJids || []).includes(p.participantJid || "")
            )}
            onChange={(e, newVals) => {
              const jids = (newVals || [])
                .map((p) => p.participantJid || "")
                .filter(Boolean);
              setArConfig((c) => ({
                ...c,
                contactJids: jids,
                contactJid: jids[0] || "",
              }));
            }}
            noOptionsText="Nenhum contato encontrado"
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Contatos monitorados"
                placeholder="Buscar contato..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleSyncParticipants}
            disabled={!arConfig.groupJid || arSyncing}
            title="Atualizar lista de participantes do grupo"
            style={{ minWidth: 44, height: 40, flexShrink: 0, borderRadius: 8 }}
          >
            {arSyncing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </Button>
        </Box>

        {(arConfig.replyTexts || [""]).map((text, index) => {
          const list = arConfig.replyTexts || [""];
          const isLast = index === list.length - 1;
          return (
            <Box key={index} className={classes.replyTextRow}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                label={
                  list.length > 1
                    ? `Texto de resposta automática ${index + 1}`
                    : "Texto de resposta automática"
                }
                placeholder="Digite o texto que será enviado automaticamente..."
                value={text}
                onChange={handleReplyTextChange(index)}
              />
              <Box className={classes.replyTextActions}>
                {isLast && (
                  <Tooltip title="Adicionar outra opção de resposta">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleAddReplyText}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {list.length > 1 && (
                  <Tooltip title="Remover esta opção">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveReplyText(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
        {(arConfig.replyTexts || [""]).length > 1 && (
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.replyTextHelper}
          >
            A cada resposta o sistema escolhe uma dessas opções aleatoriamente, sem repetir a mesma até que todas tenham sido usadas.
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          size="small"
          label="Padrões para ignorar (não responder)"
          placeholder="Ex: 🔴, URGENTE, cancelado — separados por vírgula ou linha"
          helperText="Se a mensagem recebida contiver qualquer um desses textos/emojis, a resposta automática não será enviada."
          value={arConfig.ignorePatterns}
          onChange={handleArFieldChange("ignorePatterns")}
          className={classes.autoReplyField}
        />

        <Box className={classes.alertBox}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <NotificationsActiveIcon style={{ color: orange[700] }} />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Alerta no celular
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Toca um som e notifica sempre que uma resposta automática for enviada — mesmo com o navegador fechado.
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                alertBusy ? (
                  <CircularProgress size={20} style={{ margin: 9 }} />
                ) : (
                  <Switch
                    checked={!!arConfig.alertEnabled}
                    onChange={handleAlertToggle}
                    color="primary"
                  />
                )
              }
              label=""
            />
          </Box>
        </Box>

        <Box className={classes.autoReplyActions}>
          <FormControlLabel
            control={
              <Switch
                checked={!!arConfig.disableAfterReply}
                onChange={handleToggleDisableAfter}
                color="secondary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="textSecondary">
                Desativar automaticamente após enviar
              </Typography>
            }
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={arSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAutoReply}
            disabled={arSaving}
            title="Salva os campos de texto (grupo, contato, mensagem e filtros)"
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
          >
            {arSaving ? "Salvando..." : "Salvar textos"}
          </Button>
        </Box>

        {/* ─── Desempenho e Histórico ─── */}
        <Box className={classes.historyBox}>
          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
            <HistoryIcon style={{ color: isDark ? theme.palette.text.secondary : grey[700] }} />
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Desempenho e histórico
            </Typography>
          </Box>

          {/* Contadores */}
          <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
            <Box className={`${classes.statCard} ${classes.statCardSuccess}`}>
              <CheckCircleOutlineIcon style={{ color: green[600] }} fontSize="small" />
              <Typography className={classes.statValue} style={{ color: isDark ? green[300] : green[700] }}>
                {arStats.totals.success}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Respostas primeiro
              </Typography>
            </Box>
            <Box className={`${classes.statCard} ${classes.statCardFailed}`}>
              <HighlightOffIcon style={{ color: red[500] }} fontSize="small" />
              <Typography className={classes.statValue} style={{ color: isDark ? red[300] : red[600] }}>
                {arStats.totals.failed}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Falhas
              </Typography>
            </Box>
            <Box className={`${classes.statCard} ${classes.statCardTotal}`}>
              <ReplyIcon style={{ color: isDark ? theme.palette.text.secondary : grey[600] }} fontSize="small" />
              <Typography className={classes.statValue} style={{ color: isDark ? theme.palette.text.primary : grey[800] }}>
                {arStats.totals.total}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total
              </Typography>
            </Box>
          </Box>

          {/* Filtro de datas */}
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={statsFrom}
              onChange={(e) => setStatsFrom(e.target.value)}
              style={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              type="date"
              size="small"
              variant="outlined"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={statsTo}
              onChange={(e) => setStatsTo(e.target.value)}
              style={{ flex: 1, minWidth: 130 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={loadAutoReplyStats}
              disabled={arStatsLoading}
              startIcon={arStatsLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              style={{ height: 40, borderRadius: 8, textTransform: "none" }}
            >
              Buscar
            </Button>
          </Box>

          {/* Lista do histórico */}
          {arStats.events.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ marginTop: 16, fontStyle: "italic" }}
            >
              Nenhum evento no período.
            </Typography>
          ) : (
            <Box className={classes.historyList}>
              {arStats.events.map((ev) => (
                <Box key={ev.id} className={classes.historyItem}>
                  {ev.status === "success" ? (
                    <CheckCircleOutlineIcon style={{ color: green[600] }} fontSize="small" />
                  ) : (
                    <HighlightOffIcon style={{ color: red[500] }} fontSize="small" />
                  )}
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" noWrap style={{ fontWeight: 500 }}>
                      {ev.status === "success" ? "Respondeu primeiro" : "Outro respondeu antes"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" noWrap style={{ display: "block" }}>
                      {ev.triggerBody ? `“${ev.triggerBody}”` : "—"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" style={{ whiteSpace: "nowrap" }}>
                    {formatDate(ev.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Snackbar
          open={!!arSnackbar}
          autoHideDuration={3000}
          onClose={() => setArSnackbar("")}
          message={arSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    );
  };

  // Renderizar estado de carregamento inicial
  if (loading) {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="textSecondary" style={{ marginTop: 16 }}>
              Carregando informações da conexão...
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  // Renderizar conexão não encontrada
  if (notFound) {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.notFoundContainer}>
            <ErrorIcon style={{ fontSize: 64, color: red[400], marginBottom: 16 }} />
            <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
              Conexão não encontrada
            </Typography>
            <Typography variant="body1" color="textSecondary">
              O link que você acessou é inválido ou a conexão não existe mais.
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  const { status, qrcode, name, phoneNumber, connectedNumber, isConnected, queues, createdAt, updatedAt } =
    connectionData || {};

  const displayNumber = phoneNumber || connectedNumber;

  // Nome/marca do topo — configurável por company via parâmetro
  // `public_connection_brand_name` no backend. Sem parâmetro → "FaleCRM".
  const brandName = connectionData?.brandName || "FaleCRM";

  // Lock de driver: outra aba/navegador está configurando esta conexão.
  const activeClientId = connectionData?.activeClientId || null;
  const lockedByOther = !!activeClientId && activeClientId !== clientIdRef.current;

  // Código de pareamento: backend envia "PAIR:<código>:<número>".
  const isPairCode = !!qrcode && qrcode.startsWith("PAIR:");
  const pairParts = isPairCode ? qrcode.slice(5).split(":") : [];
  const pairCode = pairParts[0] || "";
  const pairNumber = pairParts[1] || "";

  // ─── RENDERIZAR CONEXÃO CONECTADA ───
  if (status === "CONNECTED" && isConnected) {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.logo}>
            <Typography className={classes.logoText}>{brandName}</Typography>
          </div>

          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <Chip
              icon={<CheckCircleIcon />}
              label={getStatusLabel(status)}
              className={`${classes.statusChip} ${classes.connectedChip}`}
            />
          </Box>

          <Card className={classes.connectedCard} elevation={0}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar className={classes.avatarConnected}>
                  <PhoneIcon />
                </Avatar>
                <Box ml={2}>
                  <Typography className={classes.connectionName}>{name}</Typography>
                  <Typography className={classes.phoneNumber}>
                    {formatPhoneNumber(displayNumber)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card className={classes.statsCard} elevation={0}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <SignalIcon style={{ color: green[500], marginRight: 8 }} />
                      <Typography variant="body2" color="textSecondary">
                        Status
                      </Typography>
                    </Box>
                    <Typography variant="h6" style={{ fontWeight: 600, color: isDark ? green[300] : green[700] }}>
                      Online
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card className={classes.statsCard} elevation={0}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <QueueIcon style={{ color: "#667eea", marginRight: 8 }} />
                      <Typography variant="body2" color="textSecondary">
                        Filas
                      </Typography>
                    </Box>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      {queues?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card className={classes.statsCard} elevation={0}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <BusinessIcon style={{ color: grey[600], marginRight: 8 }} />
                      <Typography variant="body2" color="textSecondary">
                        Criada em
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {formatDate(createdAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card className={classes.statsCard} elevation={0}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ScheduleIcon style={{ color: grey[600], marginRight: 8 }} />
                      <Typography variant="body2" color="textSecondary">
                        Última atualização
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {formatDate(updatedAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {renderAutoReplySection()}

          <Typography className={classes.autoRefreshText}>
            Atualização automática a cada 2 segundos
          </Typography>
        </Paper>
      </div>
    );
  }

  // ─── RENDERIZAR "ABERTO EM OUTRO NAVEGADOR" (lock de driver) ───
  // Outra aba/navegador está configurando esta conexão. Evita que dois gerem
  // QR/código ao mesmo tempo (o que "buga" o código). Oferece assumir o controle.
  if (lockedByOther && status !== "CONNECTED") {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.logo}>
            <Typography className={classes.logoText}>{brandName}</Typography>
          </div>

          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <Chip
              icon={<ErrorIcon />}
              label="Aberto em outro navegador"
              className={`${classes.statusChip} ${classes.qrcodeChip}`}
            />
          </Box>

          <Typography variant="h6" align="center" gutterBottom style={{ fontWeight: 600 }}>
            {name || "Conexão WhatsApp"}
          </Typography>

          <Typography variant="body1" align="center" color="textSecondary" paragraph>
            Esta conexão já está sendo configurada em outra aba ou navegador. Para
            evitar conflito no código/QR, continue por lá — ou clique abaixo para
            trazer o controle para cá (isso vai reiniciar a geração aqui).
          </Typography>

          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleTakeOver}
              disabled={qrLoading}
              startIcon={qrLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              {qrLoading ? "Assumindo..." : "Puxar para cá"}
            </Button>
          </Box>

          <Typography className={classes.autoRefreshText}>
            Atualização automática a cada 2 segundos
          </Typography>
        </Paper>
      </div>
    );
  }

  // ─── RENDERIZAR ESTADO "CONECTANDO" ───
  // Banco marcou CONNECTED mas o socket real ainda não confirmou. Evita o chip
  // contraditório "Conectado" na tela de desconectado e mostra um estado claro.
  if (status === "CONNECTED" && !isConnected) {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.logo}>
            <Typography className={classes.logoText}>{brandName}</Typography>
          </div>

          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <Chip
              label="Conectando..."
              className={`${classes.statusChip} ${classes.qrcodeChip}`}
            />
          </Box>

          <Typography variant="h6" align="center" gutterBottom style={{ fontWeight: 600 }}>
            {name || "Conexão WhatsApp"}
          </Typography>

          <Box display="flex" flexDirection="column" alignItems="center" mt={2} mb={1}>
            <CircularProgress size={48} />
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 16 }}>
              Estabelecendo conexão com o WhatsApp. Aguarde alguns segundos...
            </Typography>
          </Box>

          {connectingEscape && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={qrLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                onClick={() => handleGenerateQrCode({ silent: false })}
                disabled={qrLoading}
                style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
              >
                {qrLoading ? "Gerando..." : "Ainda não conectou? Gerar novo QR Code"}
              </Button>
            </Box>
          )}

          {actionError && (
            <Box mt={2}>
              <Typography variant="body2" color="error" align="center">
                {actionError}
              </Typography>
            </Box>
          )}

          <Typography className={classes.autoRefreshText}>
            Atualização automática a cada 2 segundos
          </Typography>
        </Paper>
      </div>
    );
  }

  // ─── RENDERIZAR QR CODE (status qrcode ou OPENING) ───
  if (status === "qrcode" || status === "OPENING") {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.logo}>
            <Typography className={classes.logoText}>{brandName}</Typography>
          </div>

          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <Chip
              label={getStatusLabel(status)}
              className={`${classes.statusChip} ${classes.qrcodeChip}`}
            />
          </Box>

          <Typography
            variant="h6"
            align="center"
            gutterBottom
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Conecte seu WhatsApp
          </Typography>

          {/* Seletor de método: QR Code ou número de telefone */}
          <Box display="flex" justifyContent="center" gridGap={8} style={{ gap: 8, marginBottom: 16 }}>
            <Button
              size="small"
              variant={pubMethod === "qr" ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                setPubMethod("qr");
                setActionError("");
                setPubEditingNumber(false);
                // Se havia um código de pareamento ativo, regenera como QR.
                if (isPairCode) handleGenerateQrCode({ silent: false });
              }}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              QR Code
            </Button>
            <Button
              size="small"
              variant={pubMethod === "phone" ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                setPubMethod("phone");
                setActionError("");
                // Sem código ainda → abre o campo de número.
                if (!isPairCode) setPubEditingNumber(true);
              }}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              Número de telefone
            </Button>
          </Box>

          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="flex-start">
            <Box flex={1} mr={{ sm: 3 }} mb={{ xs: 2, sm: 0 }}>
              <Typography variant="body1" gutterBottom style={{ fontWeight: 500 }}>
                Siga os passos:
              </Typography>
              {pubMethod === "phone" ? (
                <>
                  <ol className={classes.instructionsList}>
                    <li>Informe o número (DDI + DDD + número) e clique em <strong>Gerar código</strong></li>
                    <li>Abra o WhatsApp no celular desse número</li>
                    <li>Toque em <strong>Dispositivos conectados</strong> → <strong>Conectar um dispositivo</strong></li>
                    <li>Toque em <strong>Conectar com número de telefone</strong></li>
                    <li>Digite o código de 8 dígitos exibido ao lado</li>
                  </ol>
                  <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", marginTop: 8 }}>
                    Use apenas dígitos, com DDI. Ex.: 5511999998888.
                  </Typography>
                </>
              ) : (
                <>
                  <ol className={classes.instructionsList}>
                    <li>Abra o WhatsApp no seu celular</li>
                    <li>Toque em <strong>Mais opções</strong> (Android) ou <strong>Configurações</strong> (iPhone)</li>
                    <li>Toque em <strong>Dispositivos conectados</strong></li>
                    <li>Toque em <strong>Conectar dispositivo</strong></li>
                    <li>Aponte seu celular para o QR Code ao lado</li>
                  </ol>
                  <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", marginTop: 8 }}>
                    Se o QR Code expirar, clique em "Gerar Novo QR-Code".
                  </Typography>
                </>
              )}
            </Box>

            <div className={classes.qrContainer}>
              {isPairCode && !pubEditingNumber ? (
                <div style={{ textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Digite este código no seu WhatsApp:
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{ fontFamily: "monospace", letterSpacing: 6, fontWeight: "bold", marginBottom: 4 }}
                  >
                    {pairCode}
                  </Typography>
                  {pairNumber && (
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                      Número: <strong>+{pairNumber}</strong>
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      // Volta a exibir o campo para conferir/alterar o número.
                      setPubPhone(pairNumber || pubPhone);
                      setPubEditingNumber(true);
                      setActionError("");
                    }}
                    disabled={qrLoading}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
                  >
                    Gerar novo código
                  </Button>
                </div>
              ) : pubMethod === "phone" ? (
                <div style={{ textAlign: "center", minWidth: 220 }}>
                  <TextField
                    label="Número com DDI"
                    placeholder="5511999998888"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={pubPhone}
                    onChange={(e) => setPubPhone(e.target.value.replace(/\D/g, ""))}
                    disabled={qrLoading}
                    inputProps={{ inputMode: "numeric" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRequestPairingCode}
                    disabled={qrLoading}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, marginTop: 12 }}
                  >
                    {qrLoading ? "Gerando..." : "Gerar código"}
                  </Button>
                  {qrLoading && (
                    <div style={{ marginTop: 16 }}>
                      <CircularProgress size={28} />
                    </div>
                  )}
                </div>
              ) : qrcode === "EXPIRED" || qrStaleLocal ? (
                <>
                  <Typography variant="h6" align="center" gutterBottom>
                    QR Code expirado.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 12 }}>
                    Clique abaixo para gerar um novo.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={qrLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={() => handleGenerateQrCode({ silent: false })}
                    disabled={qrLoading}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
                  >
                    {qrLoading ? "Gerando..." : "Gerar Novo QR-Code"}
                  </Button>
                </>
              ) : qrcode && qrcode.startsWith("COOLDOWN:") ? (
                <>
                  <Typography variant="h6" align="center" gutterBottom>
                    Muitas tentativas de conexão
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 12, maxWidth: 300 }}>
                    Para proteger seu número contra bloqueio do WhatsApp, a
                    geração de QR Code foi pausada e volta sozinha às{" "}
                    <strong>
                      {(() => {
                        const d = new Date(qrcode.slice(9));
                        return Number.isNaN(d.getTime())
                          ? "—"
                          : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                      })()}
                    </strong>
                    .
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 12, maxWidth: 300 }}>
                    Se preferir, tente agora — mas evite gerar muitos QR Codes
                    seguidos sem escanear.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={qrLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={() => handleGenerateQrCode({ silent: false })}
                    disabled={qrLoading}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
                  >
                    {qrLoading ? "Gerando..." : "Tentar agora"}
                  </Button>
                </>
              ) : status === "OPENING" || (status === "qrcode" && !qrcode) ? (
                <>
                  <CircularProgress size={48} />
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                    Gerando QR Code...
                  </Typography>
                </>
              ) : qrcode ? (
                <div className={classes.qrWrapper}>
                  <QRCode value={qrcode} size={220} level="M" />
                </div>
              ) : (
                <>
                  <CircularProgress size={48} />
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                    Aguardando...
                  </Typography>
                </>
              )}
            </div>
          </Box>

          {actionError && (
            <Box mt={2}>
              <Typography variant="body2" color="error" align="center">
                {actionError}
              </Typography>
            </Box>
          )}

          {renderAutoReplySection()}

          <Typography className={classes.autoRefreshText}>
            Atualização automática a cada 2 segundos
          </Typography>
        </Paper>
      </div>
    );
  }

  // ─── RENDERIZAR DESCONECTADO ───
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.logo}>
          <Typography className={classes.logoText}>{brandName}</Typography>
        </div>

        <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
          <Chip
            icon={<ErrorIcon />}
            label={getStatusLabel("DISCONNECTED")}
            className={`${classes.statusChip} ${classes.disconnectedChip}`}
          />
        </Box>

        <Typography variant="h6" align="center" gutterBottom style={{ fontWeight: 600 }}>
          {name || "Conexão WhatsApp"}
        </Typography>

        <Typography variant="body1" align="center" color="textSecondary" paragraph>
          {pubMethod === "phone"
            ? "Esta conexão está desconectada. Informe o número de telefone para receber um código de pareamento."
            : "Esta conexão está desconectada. Para conectar, escaneie o QR Code com seu WhatsApp."}
        </Typography>

        {/* Seletor de método: QR Code ou número de telefone */}
        <Box display="flex" justifyContent="center" style={{ gap: 8, marginBottom: 8 }}>
          <Button
            size="small"
            variant={pubMethod === "qr" ? "contained" : "outlined"}
            color="primary"
            onClick={() => { setPubMethod("qr"); setActionError(""); }}
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
          >
            QR Code
          </Button>
          <Button
            size="small"
            variant={pubMethod === "phone" ? "contained" : "outlined"}
            color="primary"
            onClick={() => { setPubMethod("phone"); setActionError(""); }}
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
          >
            Número de telefone
          </Button>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" mt={2}>
          {pubMethod === "phone" ? (
            <div style={{ width: "100%", maxWidth: 280, textAlign: "center" }}>
              <TextField
                label="Número com DDI"
                placeholder="5511999998888"
                variant="outlined"
                margin="dense"
                fullWidth
                value={pubPhone}
                onChange={(e) => setPubPhone(e.target.value.replace(/\D/g, ""))}
                disabled={qrLoading}
                inputProps={{ inputMode: "numeric" }}
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleRequestPairingCode}
                disabled={qrLoading}
                startIcon={qrLoading ? <CircularProgress size={20} color="inherit" /> : null}
                style={{ marginTop: 12, borderRadius: 8, textTransform: "none", fontWeight: 600 }}
              >
                {qrLoading ? "Gerando..." : "Gerar código"}
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={qrLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={() => handleGenerateQrCode({ silent: false })}
                disabled={qrLoading}
                style={{ marginBottom: 12, borderRadius: 8, textTransform: "none", fontWeight: 600 }}
              >
                {qrLoading ? "Gerando..." : "Gerar QR Code"}
              </Button>

              {connectionData?.hasSession && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleReconnect}
                  disabled={qrLoading}
                  style={{ borderRadius: 8, textTransform: "none" }}
                >
                  Tentar Reconectar
                </Button>
              )}
            </>
          )}
        </Box>

        {actionError && (
          <Box mt={2}>
            <Typography variant="body2" color="error" align="center">
              {actionError}
            </Typography>
          </Box>
        )}

        {renderAutoReplySection()}

        <Typography className={classes.autoRefreshText}>
          Atualização automática a cada 2 segundos
        </Typography>
      </Paper>
    </div>
  );
};

export default PublicConnectionPage;
